/** @format */
import isPropValid from '@emotion/is-prop-valid';
import { useRouter } from 'next/router';
import React, { FunctionComponent, useEffect } from 'react';
import styled from 'styled-components';

import { ModelSearchResultsType } from '@toucan/common/services/search/constants';

import { setSearchParamsForSearchResults } from '@toucan/client/global/nextjs';

import { ConstrainedWrapper } from '@toucan/client/shared/components/constrained-wrapper';

import { PaginationVariant, SearchResultsVariant } from '../search-results';
import { Filters } from './components/filters';
import { OutOfStockRecommendations } from './components/out-of-stock-recommendations';
import { PaginationControls } from './components/pagination-controls';
import { SearchResultsDisplay } from './components/search-results-display';
import { Sort } from './components/sort';
import { SpellingSuggestion } from './components/spelling-suggestion';
import { DEFAULT_TEST_ID } from './constants';
import { useModelCards } from './hooks/use-model-cards';
import { useModelSearchResults } from './hooks/use-model-search-results';
import { useSearchParams } from './hooks/use-search-params';
import { useSearchResultsInfiniteScroll } from './hooks/use-search-results-infinite-scroll';
import { GridWrapper, styles } from './model-search-results.styles';
import { ModelSearchResultsProps } from './types';

export { ModelSearchResultsType };

// eslint-disable-next-line @mpb-com/mpb/require-types-file -- Legacy code: do not use as an example.
export type StyledModelSearchResultsProps = Pick<
    ModelSearchResultsProps,
    'isLoading'
>;

const StyledModelSearchResults = styled('div').withConfig({
    shouldForwardProp: (prop) => isPropValid(prop),
})`
    ${styles}
`;

export const ModelSearchResults: FunctionComponent<ModelSearchResultsProps> = ({
    baseQuery = {},
    resultsPerPage = 52,
    onCreateAlert,
    // eslint-disable-next-line @typescript-eslint/no-unused-vars -- Legacy code: do not use as an example.
    showProductNames = false,
    getDefaultSortOption,
    paginationVariant,
    type,
    isLoading = false,
    'data-testid': testId = DEFAULT_TEST_ID,
    // eslint-disable-next-line @typescript-eslint/no-unused-vars -- Legacy code: do not use as an example.
    isAccessoriesFilterEnabled = false,
    // eslint-disable-next-line @typescript-eslint/no-unused-vars -- Legacy code: do not use as an example.
    isAutoOutOfStockSearchEnabled = false,
    ...otherProps
}) => {
    const isSearchPage = type === ModelSearchResultsType.SEARCH;

    const disallowedFilters = [
        'modelType',
        'productPriceModifierIds', // We don't want to show the accessory filters on the model search results page
        ...(otherProps.disallowedFilters ?? []),
    ];
    const searchResultsVariant = SearchResultsVariant.MODEL;
    const selectedPaginationVariant =
        paginationVariant ?? PaginationVariant.PAGE_SELECT;

    const router = useRouter();

    const searchParams = useSearchParams();

    const {
        pageIndex,
        // eslint-disable-next-line @typescript-eslint/naming-convention -- Legacy code: do not use as an example.
        showAll,
        selectedFilters,
        sort = {},
        originalQuery,
        // eslint-disable-next-line @typescript-eslint/naming-convention -- Legacy code: do not use as an example.
        outOfStock,
        // eslint-disable-next-line @typescript-eslint/naming-convention -- Legacy code: do not use as an example.
        noInStockResults,
    } = searchParams;

    const isInfiniteScroll = showAll;

    const {
        results: searchResults,
        isLoading: isSearchResultsLoading,
        isFetchingNextPage,
        hasNextPage,
        fetchNextPage,
    } = useModelSearchResults(
        baseQuery,
        isInfiniteScroll,
        resultsPerPage,
        type
    );

    const isLoadingResults =
        isSearchResultsLoading ||
        isFetchingNextPage ||
        searchResults?.totalResults === -1;

    const numResultSkeletons = isLoadingResults ? resultsPerPage : 0;

    const triggerRef = useSearchResultsInfiniteScroll({
        showAll,
        hasNextPage,
        fetchNextPage,
    });

    function handleShowAllResults(): void {
        setSearchParamsForSearchResults(router, {
            showAll: true,
            page: undefined,
        });
    }

    const contentfulTileOffset = showAll ? 0 : pageIndex * resultsPerPage;

    const modelCards = useModelCards(
        searchResults,
        numResultSkeletons,
        isSearchResultsLoading,
        contentfulTileOffset,
        onCreateAlert
    );

    useEffect(() => {
        if (!searchResults || searchResults.totalResults === -1) return;

        const suggestion = searchResults?.spellcheck?.suggestions?.[0];

        if (
            baseQuery.query &&
            baseQuery.query !== originalQuery &&
            suggestion &&
            suggestion !== originalQuery
        ) {
            setSearchParamsForSearchResults(router, {
                q: suggestion,
                originalQuery: baseQuery.query,
            });
            return;
        }

        if (
            isAutoOutOfStockSearchEnabled &&
            isSearchPage &&
            !Object.keys(selectedFilters).length &&
            searchResults.results.length === 0 &&
            !outOfStock
        ) {
            setSearchParamsForSearchResults(router, {
                ...router.query,
                outOfStock: 'true',
                noInStockResults: 'true',
            });
            return;
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps -- Legacy code: do not use as an example.
    }, [searchResults]);

    return (
        <StyledModelSearchResults data-testid={testId} {...otherProps}>
            <ConstrainedWrapper>
                <SpellingSuggestion
                    baseQuery={baseQuery}
                    originalQuery={originalQuery}
                    testId={testId}
                />
                <GridWrapper>
                    <Filters
                        type={type}
                        baseQuery={baseQuery}
                        className="filters"
                        testId={testId}
                        searchResults={searchResults}
                        disallowedFilters={disallowedFilters}
                    />
                    <Sort
                        className="sort"
                        type={type}
                        outOfStock={outOfStock}
                        showAll={showAll}
                        sort={sort}
                        getDefaultSortOption={getDefaultSortOption}
                    />
                    <PaginationControls
                        className="pagination-controls"
                        paginationVariant={selectedPaginationVariant}
                        displayAllResults={showAll}
                        isLoading={isLoading}
                        totalResultsCount={searchResults.totalResults}
                        resultsInViewCount={searchResults.resultCount}
                        onSeeAllClick={handleShowAllResults}
                    />
                    <SearchResultsDisplay
                        className="search-results"
                        isLoading={isLoading}
                        paginationVariant={selectedPaginationVariant}
                        resultsInView={modelCards}
                        displayAllResults={showAll}
                        totalResultsCount={searchResults.totalResults}
                        resultsInViewCount={searchResults.resultCount}
                        page={pageIndex}
                        variant={searchResultsVariant}
                        onCreateAlert={onCreateAlert}
                        onSeeAllClick={handleShowAllResults}
                        data-testid={`${testId}__search-results`}
                        resultsPerPage={resultsPerPage}
                        displayOutOfStock={outOfStock}
                        noInStockResults={noInStockResults}
                    />
                </GridWrapper>

                <div ref={triggerRef} />

                {isSearchPage ? (
                    <OutOfStockRecommendations
                        searchResults={searchResults}
                        noInStockResults={noInStockResults}
                    />
                ) : null}
            </ConstrainedWrapper>
        </StyledModelSearchResults>
    );
};
